import { useEffect, useState } from 'react';
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react';

const springPop = { type: 'spring' as const, stiffness: 500, damping: 22 };
const countSpring = { type: 'spring' as const, stiffness: 200, damping: 24, mass: 0.55 };

interface AnimatedInsightNumberProps {
  value: number;
  className?: string;
}

export default function AnimatedInsightNumber({ value, className }: AnimatedInsightNumberProps) {
  const [hydrated, setHydrated] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const count = useMotionValue(0);
  const formatted = useTransform(count, (v) => Math.round(v).toLocaleString('en-US'));

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    if (prefersReducedMotion) {
      count.set(value);
      return;
    }

    const controls = animate(count, value, countSpring);
    return () => controls.stop();
  }, [count, value, prefersReducedMotion, hydrated]);

  const baseClass = `tabular-nums${className ? ` ${className}` : ''}`;
  const stableLabel = value.toLocaleString('en-US');

  if (!hydrated) {
    return <span className={baseClass}>{stableLabel}</span>;
  }

  if (prefersReducedMotion) {
    return <span className={baseClass}>{stableLabel}</span>;
  }

  return (
    <motion.span
      initial={{ scale: 0.4, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={springPop}
      className={`inline-block origin-center ${baseClass}`}
    >
      <motion.span>{formatted}</motion.span>
    </motion.span>
  );
}
